import { toast } from 'react-toastify';

import {
  enviarVeiculoAoSga,
  reconciliarVeiculo,
  trazerVeiculoDoSga,
} from '../api/sga-reconciliacao/sgaReconciliacaoApi';
import { DivergenciaSga } from '../api/sga-divergencia/sgaDivergenciaApi';
import { extrairMensagemErro } from './apiError';

/**
 * Os dois sentidos do espelho SAAC ↔ SGA (at-32), num lugar só.
 *
 * A função vivia **duplicada** em `/carro/substituir` e `/vistoria/captura`, com o
 * mesmo corpo — e o defeito corrigido na at-47 estava nas duas cópias. Extraída pela
 * convenção da at-24: regra repetida em componente é divergência com data marcada.
 */

export type AcaoSincronizacaoSga = 'TRAZER' | 'ENVIAR';

/**
 * Executa a ação e devolve a lista de divergências que deve ficar na faixa.
 *
 * ⚠️ **Os dois sentidos NÃO se atualizam do mesmo jeito, e essa é a correção da
 * at-47 (bug B8):**
 *
 * - **TRAZER** (SGA → local) escreve no **banco local** e não toca no SGA. Reconciliar
 *   logo depois é correto: compara o local recém-atualizado com o SGA inalterado.
 * - **ENVIAR** (local → SGA) escreve **no SGA**, e a leitura de lá volta **defasada**
 *   (medido em 06/09/2026 no veículo de teste 96642: 3–4s após um `alterar/veiculo`
 *   bem-sucedido a busca ainda devolve os valores antigos; 60s depois traz os novos).
 *   Reconciliar na sequência comparava o cadastro local **já enviado** contra o dado
 *   **velho** do SGA e **reabria as divergências que o backend acabara de fechar** —
 *   o consultor via "Cadastro enviado ao SGA" com a faixa intacta na tela, como se
 *   nada tivesse acontecido. Por isso aqui a faixa é atualizada com os
 *   `idsFechados` que o próprio backend devolve, sem nova leitura do SGA.
 *
 * Esperar os 60s antes de reconciliar seria pior: travaria a tela por um minuto para
 * chegar à mesma lista que o backend já sabe informar.
 *
 * ⚠️ **Lança** em falha (as duas rotas são escritas pedidas pelo usuário). Quem chama
 * decide o que fazer; `mensagemDeErroSincronizacao` padroniza o texto.
 */
export async function sincronizarVeiculoComSga(
  acao: AcaoSincronizacaoSga,
  carroId: string,
  divergenciasAtuais: DivergenciaSga[],
): Promise<DivergenciaSga[]> {
  if (acao === 'TRAZER') {
    const r = await trazerVeiculoDoSga(carroId);

    toast.success(
      r.aplicados.length
        ? `${r.aplicados.length} campo(s) atualizado(s) com os dados do SGA.`
        : 'Nenhum campo pôde ser trazido automaticamente.',
    );
    if (r.pendentes.length) {
      toast.info(`${r.pendentes.length} diferença(s) exigem a equipe de TI.`);
    }

    const atualizado = await reconciliarVeiculo(carroId);
    return atualizado.divergencias;
  }

  const r = await enviarVeiculoAoSga(carroId);

  const fechados = new Set(r.idsFechados ?? []);
  const restantes = divergenciasAtuais.filter((d) => !fechados.has(d.id));

  toast.success(
    fechados.size
      ? `Cadastro enviado ao SGA. ${fechados.size} diferença(s) resolvida(s).`
      : r.mensagem || 'Cadastro enviado ao SGA.',
  );

  // Campo com valor local vazio/zero: o SGA ignora, então continua divergente
  // (at-47, B9). O usuário precisa saber que aquilo NÃO foi resolvido.
  if (r.naoAceitosPeloSga?.length) {
    toast.warn(
      `${r.naoAceitosPeloSga.join(', ')}: o SGA não aceita valor vazio ou zero — ` +
        'preencha o dado no SAAC e envie de novo, ou ajuste direto no SGA.',
    );
  }

  // O que sobra é estrutural (troca de titular, carteira, códigos únicos): o backend
  // não fecha esses no envio de propósito — são decisão do TI, não "campo".
  const estruturais = restantes.length - (r.naoAceitosPeloSga?.length ?? 0);
  if (estruturais > 0) {
    toast.info(`${estruturais} diferença(s) exigem a equipe de TI.`);
  }

  return restantes;
}

export const mensagemDeErroSincronizacao = (erro: unknown) =>
  extrairMensagemErro(erro, 'Não foi possível sincronizar com o SGA.');
